import type { Cancellable } from '../../../types';
import type { ReactElement } from 'react';

import { AspectRatio, Box, Button, Card, Flex, Heading, IconButton, Text } from '@rango-dev/whale';
import { Check } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { useProfileWalletFlow } from '../../../hooks/useProfileWalletFlow';
import {
  clearAuthError,
  fetchPublicLeaderboard,
  requestLogOutUser,
} from '../../../state/ProfileSlice';
import { Campaigns } from '../../CampaignsSidebar/Campaigns';
import { Image } from '../../common/Image';
import { SimpleNotification } from '../../SimpleNotification';
import { Leaderboard } from '../Leaderboard';

import styles from './LogIn.module.css';

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const HERO_RATIO = 360 / 203;

export function LogIn(): ReactElement {
  const loadingMetaStatus = useAppSelector((state) => state.meta.loadingStatus);
  const authError = useAppSelector((state) => state.profile.profileAuth.error);
  const username = useAppSelector((state) => state.profile.username);
  const isLoggedIn = useAppSelector((state) => state.profile.isLoggedIn);
  const { isDarkMode } = useAppSelector((state) => state.settings);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { readyToSign, openProfileWalletFlow } = useProfileWalletFlow();

  const fetchPublicLeaderboardPromise = useRef<Cancellable | null>(null);

  const {
    data: publicLeaderboard,
    loading: publicLeaderboardLoading,
    error: publicLeaderboardError,
  } = useAppSelector((state) => state.profile.publicLeaderboard);
  const metaTitle = t('Rango Exchange | Rango Profile - View Your Score & Ranking');
  const metaDescription = t(
    'Check your Rango Exchange profile to see your score, trading volume, and ranking among Rangonauts! View your most used Blockchain, DEX or Bridge!',
  );

  const signWalletDisabled = !readyToSign;
  const walletButtonId = `profile-page-${readyToSign ? 'sign' : 'connect'}-wallet-btn`;
  const heroImageSrc = `/imgs/profile/profile-overview-${isDarkMode ? 'dark' : 'light'}.png`;

  const handleLogOut = async () => {
    void dispatch(requestLogOutUser());
  };

  const fetchLeaderboard = () => {
    fetchPublicLeaderboardPromise.current = dispatch(
      fetchPublicLeaderboard({ page: 0, offset: 10 }),
    );
  };

  useEffect(() => {
    if (!isLoggedIn) {
      fetchLeaderboard();
    }
    return () => {
      fetchPublicLeaderboardPromise.current?.abort();
    };
  }, [isLoggedIn]);

  if (isLoggedIn && username) {
    return <Navigate to={username} replace />;
  }

  if (isLoggedIn) {
    void handleLogOut();
  }

  return (
    <Flex direction={{ initial: 'column', lg: 'row' }} gap="4">
      <Helmet>
        <title>{metaTitle}</title>
        <meta property="og:title" content={metaTitle} />
        <meta name="twitter:title" content={metaTitle} />

        <meta name="description" content={metaDescription} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <SimpleNotification
        message={t('An error occurred during login. Please try again.')}
        open={!!authError}
        setOpen={(open) => {
          if (!open) {
            dispatch(clearAuthError());
          }
        }}
        severity="error"
      />
      <Card size={{ initial: '3', sm: '4' }} variant="surface" className={styles.grow}>
        <Flex direction="column" align="center" gap="5">
          <Box width="100%" maxWidth="360px">
            <AspectRatio ratio={HERO_RATIO}>
              <Image
                className={styles.heroImage}
                alt="authenticated profile image"
                title="authenticated profile image"
                src={heroImageSrc}
              />
            </AspectRatio>
          </Box>
          <Flex direction="column" gap="6">
            <Flex direction="column" align="center" gap="1">
              <Heading size="5" weight="bold" align="center">
                {t('Sign wallet to see your stats!')}
              </Heading>
              <Text size="2" color="gray" align="center">
                {t('Track your score and stats in your profile')}
              </Text>
            </Flex>
            <Flex justify="center" pt="4" pb="6">
              <Flex direction="column" align="center" gap="1" position="relative">
                <IconButton
                  id="profile-page-step-1"
                  radius="full"
                  variant={readyToSign ? 'solid' : 'outline'}
                  size="2"
                  color="cyan"
                  aria-label={t('Connect wallet')}>
                  {readyToSign ? (
                    <Check size={16} />
                  ) : (
                    <Text size="2" weight="medium">
                      1
                    </Text>
                  )}
                </IconButton>
                <Box position="absolute" top="36px" width="max-content">
                  <Text size="1" weight="regular" color="gray">
                    {t('Connect wallet')}
                  </Text>
                </Box>
              </Flex>
              <Flex width="114px" height="32px" align="center">
                <Box width="100%" height="1px" className={styles.dashedDivider} />
              </Flex>
              <Flex direction="column" align="center" gap="1" position="relative">
                <IconButton
                  id="profile-page-step-2"
                  radius="full"
                  variant="outline"
                  size="2"
                  color={signWalletDisabled ? 'gray' : 'cyan'}
                  disabled={signWalletDisabled}
                  aria-label={t('Sign wallet')}>
                  <Text size="2" weight="medium">
                    2
                  </Text>
                </IconButton>
                <Box position="absolute" top="36px" width="max-content">
                  <Text size="1" weight="regular" color="gray">
                    {t('Sign wallet')}
                  </Text>
                </Box>
              </Flex>
            </Flex>
            <Button
              id={walletButtonId}
              radius="full"
              size="3"
              variant="solid"
              color="cyan"
              disabled={loadingMetaStatus !== 'success'}
              onClick={openProfileWalletFlow}>
              {signWalletDisabled ? t('Connect your wallet') : t('Sign your wallet')}
            </Button>
          </Flex>
        </Flex>
      </Card>
      <Flex
        direction="column"
        gap={{ initial: '4', md: '5' }}
        flexShrink="0"
        width={{ initial: '100%', lg: '424px' }}>
        <Leaderboard
          isPublic
          title={t('Score Leaderboard')}
          list={publicLeaderboard}
          loading={publicLeaderboardLoading}
          error={publicLeaderboardError}
          onRetry={fetchLeaderboard}
        />
        <Campaigns />
      </Flex>
    </Flex>
  );
}
